import React, { useState, useEffect } from 'react'

import { BrManageContentButton } from '@bloomreach/react-sdk'
import DOMPurify from 'dompurify'
import BrightcovePlayer from '../brightcovePlayer/BrightcovePlayer.jsx'

// import { Link } from '@reach/router'
import { Link } from 'react-router-dom'

import { encodeForURL } from '../../../global/formatters/string-formatter.js'

import {
  alignmentMap,
  buttonMap,
  layoutMap,
  colorMap,
  getLink,
  getLinkFromRef,
  getLinkUrl,
  isPdfUrl,
  isRoutedUrl,
  getColor,
  removeSpaces,
  useTaxonomies,
  tryParseJSON,
  getSiteName,
  getLocalPath,
  BRIGHTCOVE_VIDEO_PLAYER_ID,
  BRIGHTCOVE_VIDEO_BLOG_PLAYER_ID,
  analyticsAttrFormatter,
  getMargins,
} from '../../utility'

import { Title, Subtitle, Link2, LinkList, IconText } from '..'

import './articledetail.css'

export function ArticleDetail({ page, component, item, params }) {
  const taxonomies = useTaxonomies()

  const [parameters, setParameters] = useState({
    layout: null,
    alignment: null,
    style: null,
    titleStyle: null,
    backgroundColor: null,
    subscription: null,
    derived: null,
    marginTop: null,
    marginBottom: null,
  })

  const [styles, setStyles] = useState({
    backgroundColor: null,
    textColor: null,
    topMargin: null,
    alignment: null,
    textAlign: undefined,
    capitalization: null,
    button: null,
    append: null,
    hover: null,
  })

  const [blurb, setBlurb] = useState({
    id: null,
    document: null,
    title: null,
    subtitle: null,
    subtitle2: null,
    publicationDate: null,
    time: null,
    summary: null,
    html: null,
    imageUrl: null,
    imageDescription: null,
    itemType: null,
    itemPrefix: null,
    link: null,
    linkList: null,
  })

  const _parameters = params || component?.getParameters()

  // console.log('ARTICLE PARAMETERS >>>>', _parameters, item)

  useEffect(() => {
    const _models = component?.getModels() || {}

    // console.log('ARTICLE MODELS >>>>', models)

    const { document: _documentRef } = _models

    // console.log('ARTICLE DOCUMENTREF >>>>', _documentRef)

    const _document = _documentRef && page?.getContent(_documentRef)

    // console.log('ARTICLE DOCUMENT >>>>', document)

    setParameters({
      layout: _parameters?.layout,
      alignment: _parameters?.alignment,
      style: _parameters?.style,
      titleStyle: _parameters?.titleStyle,
      backgroundColor: _parameters?.backgroundColor,
      subscription: _parameters?.subscription,
      derived: _parameters?.derived,
      forceH2: _parameters?.forceH2,
      marginTop: _parameters.marginTop || 'Large',
      marginBottom: _parameters.marginBottom || 'Large',
    })

    setStyles({
      ...colorMap[_parameters?.backgroundColor || 'None'],
      ...alignmentMap[_parameters?.alignment || 'Left'],
      ...buttonMap[_parameters?.alignment || 'Left'],
      ...layoutMap[_parameters?.layout],
    })

    if (item) {
      setBlurb(item)
    } else {
      const translatedData = {}

      if (_document) {
        const data = _document.getData()

        translatedData.title = data.title
        translatedData.subtitle = data.subtitle || data.subhead
        translatedData.subtitle2 = data.subtitle2
        translatedData.publicationDate = data.publicationDate
        translatedData.time = data.time
        translatedData.summary =
          data.summary && typeof data.summary === 'object'
            ? data.summary.value
            : data.summary
        translatedData.html =
          data.html && typeof data.html === 'object'
            ? data.html.value
            : data.html || data.content
        translatedData.imageRef = data.image || data.heroImage
        translatedData.keys = data.keys
        translatedData.itemType = data.itemType
        translatedData.itemPrefix = data.itemPrefix
        translatedData.link = data.linkCompound
        translatedData.linkList = data.linkList
      }

      const {
        title,
        subtitle,
        subtitle2,
        publicationDate,
        time,
        summary,
        html,
        imageRef,
        keys,
        itemType,
        itemPrefix,
        link,
        linkList,
      } = translatedData

      const image = imageRef && page?.getContent(imageRef)

      const imageUrl = image?.getOriginal().getUrl()
      const imageDescription = image?.getDescription()

      setBlurb({
        id: null,
        document: _document,
        title: title,
        subtitle: subtitle,
        subtitle2: subtitle2,
        publicationDate: publicationDate,
        time: time,
        summary: summary,
        html: html,
        imageUrl: imageUrl,
        imageDescription: imageDescription,
        keys: keys,
        itemType: itemType,
        itemPrefix: itemPrefix,
        link: getLink(link, null),
        linkList: linkList?.links
          .filter((l) => l.link && l.link.length > 0)
          .map((l) => getLink(getLinkFromRef(l, page), null)),
      })
    }
  }, [page, component, component?.model, item, params])

  const renderTitle = (isLink) => {
    return (
      <Title
        text={blurb.title}
        textColor={
          blurb.imageUrl && parameters.derived?.isInset
            ? 'u-textColorWhite'
            : parameters.derived?.isCard
              ? 'u-textColorBrandLight'
              : item && parameters.derived?.isLinks
                ? isLink
                  ? 'u-textColorBrandLight'
                  : 'u-textColorLight'
                : parameters.derived?.isGallery
                  ? 'u-textColorBrand'
                  : styles.textColor
        }
        lineColor={getColor(blurb.keys)}
        data-analytics={
          parameters.derived?.isCard ? 'insights-card-headline' : undefined
        }
        mode={
          item
            ? parameters.forceH2
              ? 'medium-large-h2'
              : parameters.derived?.isLinks
                ? isLink
                  ? 'tiny'
                  : parameters.derived?.isAccordion &&
                      (blurb.summary || blurb.html)
                    ? 'small-caps'
                    : 'tiny'
                : parameters.derived?.isGallery
                  ? parameters.derived?.isShortTitle
                    ? 'large'
                    : 'medium-large'
                  : parameters.derived?.isSteps
                    ? 'medium'
                    : 'small'
            : parameters.derived?.isSteps
              ? 'medium'
              : getColor(blurb.keys)
                ? 'medium-line-left'
                : parameters.titleStyle === 'Underline'
                  ? 'medium-line'
                  : parameters.titleStyle === 'Title Case'
                    ? 'title-case'
                    : parameters.titleStyle === 'Default'
                      ? 'title-default'
                      : parameters.derived?.isLinks &&
                          parameters.derived?.isAccordion
                        ? 'medium-large'
                        : 'medium'
        }
        textAlign={styles.textAlign}
        textSize={
          item && parameters.derived?.isLinks && !isLink
            ? 'u-textSizeMsrp2'
            : ''
        }
      />
    )
  }

  const onKeyPressSearch = (e, url) => {
    if (e.key === 'Enter' && url) {
      location.href = url
    }
  }

  // Example: { name: 'BlogSearch', type: 'url', placeholder: 'Search insights', variable: 'search' }
  const _titleObject = tryParseJSON(blurb.title)
  const _title = _titleObject ? _titleObject.name : blurb.title
  const isGlobalCup = window.location.pathname === '/globalcup'
  const _href =
    blurb.link?.url || blurb.link?.href
      ? blurb.link.url.startsWith('mailto:')
        ? blurb.link.url
        : isGlobalCup
          ? getLinkUrl(blurb.link)
          : getLinkUrl(blurb.link, _title)
      : undefined

  return (
    <div
      className={removeSpaces(
        `\
          ArticleDetail2 \
          u-posRelative \
          u-flex \
          ${
            parameters.style !== 'Icon'
              ? parameters.style !== 'Callout'
                ? 'u-flexCol'
                : 'u-sm-flexCol u-md-flexCol'
              : ''
          } \
          ${parameters.style !== 'Icon' ? styles.alignment : ''} \
          ${!parameters.derived?.isList ? 'u-sizeConstrained' : ''} \
          ${
            !parameters.derived?.isList
              ? getMargins(parameters.marginTop, parameters.marginBottom)
              : ''
          } \
          ${
            item || !(item && parameters.derived?.isLinks)
              ? 'u-flexAuto u-sizeFull'
              : 'u-size1of2 u-paddingRight2gu'
          } \
          `
      )}
    >
      {blurb.document && <BrManageContentButton content={blurb.document} />}
      {blurb.imageUrl &&
        parameters.style !== 'Text' &&
        (!(item && parameters.derived?.isLinks) ||
          parameters.style === 'Icon') && (
          <div
            className={removeSpaces(
              `\
                ${
                  parameters.style === 'Gray' || parameters.style === 'Icon'
                    ? 'u-flex u-flexAlignItemsCenter'
                    : ''
                } \
                ${
                  parameters.style === 'Gray'
                    ? 'u-flexCol u-bgColorNeutral u-padding2gu'
                    : ''
                } \
                `
            )}
            style={{
              width: parameters.style === 'Icon' && !_titleObject ? 16 : null,
            }}
          >
            {_href ? (
              !isRoutedUrl(_href) ? (
                <a
                  href={encodeForURL(_href)}
                  className="u-block u-posRelative u-flex u-flexAlignItemsCenter"
                  target={isPdfUrl(_href) ? '_blank' : '_self'}
                  rel="noreferrer"
                  aria-label={blurb.title}
                >
                  <img
                    className="u-sizeFull"
                    src={encodeForURL(blurb.imageUrl)}
                    alt=""
                    data-analytics={
                      parameters.derived?.isCard
                        ? 'insights-card-img'
                        : undefined
                    }
                  />
                  {parameters.derived?.isInset && (
                    <div
                      className="u-posAbsolute"
                      style={{
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      }}
                    ></div>
                  )}
                </a>
              ) : (
                <Link
                  to={_href}
                  state={{ doScroll: true }}
                  className="u-block u-flex u-flexAlignItemsCenter"
                  target={isPdfUrl(_href) ? '_blank' : '_self'}
                  aria-label={blurb.subtitle}
                >
                  <img
                    className="u-sizeFull"
                    src={encodeForURL(blurb.imageUrl)}
                    alt={blurb?.link.text || ''}
                    data-analytics={
                      parameters.derived?.isCard
                        ? 'insights-card-img'
                        : undefined
                    }
                  />
                  {parameters.derived?.isInset && (
                    <div
                      className="u-posAbsolute"
                      style={{
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      }}
                    ></div>
                  )}
                </Link>
              )
            ) : (
              <img
                className="u-sizeFull"
                src={encodeForURL(blurb.imageUrl)}
                alt={blurb.title || ''}
                data-analytics={
                  parameters.derived?.isCard ? 'insights-card-img' : undefined
                }
              />
            )}
            {blurb.title && parameters.style === 'Gray' && (
              <div className="u-marginTop2gu">
                <Title
                  text={blurb.title}
                  mode="small-caps"
                  textAlign="u-textCenter"
                />
              </div>
            )}
          </div>
        )}
      <div
        className={removeSpaces(
          `\
            u-flex \
            u-flexCol \
            u-flexAuto \
            u-sizeFull \
            ${
              parameters.derived?.isSteps ||
              parameters.derived?.isCard ||
              parameters.derived?.isInset
                ? 'u-padding4gu'
                : parameters.derived?.isGallery
                  ? 'u-marginTop2gu'
                  : (item && parameters.derived?.isLinks) ||
                      parameters.derived?.isList
                    ? ''
                    : 'u-paddingVert4gu'
            } \
            ${
              blurb.imageUrl && parameters.derived?.isInset
                ? 'u-posAbsolute'
                : ''
            } \
            ${
              blurb.imageUrl
                ? parameters.style === 'Callout'
                  ? 'u-marginLeft4gu'
                  : parameters.style === 'Icon'
                    ? 'u-marginLeft2gu'
                    : ''
                : ''
            } \
            ${styles.alignment || ''} \
            `
        )}
        style={{
          bottom: blurb.imageUrl && parameters.derived?.isInset ? 0 : null,
        }}
      >
        {blurb.videoId && (
          <div className="video-player u-sizeFull u-marginBottom4gu">
            <div className="player-container">
              <BrightcovePlayer
                accountId={
                  blurb.videoId?.indexOf('|') && blurb.videoId.split('|')[1]
                    ? blurb.videoId.split('|')[1].trim()
                    : window.BRIGHTCOVE_PLAYER_ACCOUNT_ID
                }
                videoId={
                  blurb.videoId?.indexOf('|')
                    ? blurb.videoId.split('|')[0].trim()
                    : blurb.videoId
                } // e.g. '6259022897001'
                playerId={
                  blurb.videoId?.indexOf('|') && blurb.videoId.split('|')[1]
                    ? blurb.videoId.split('|')[1].trim() ===
                      window.BRIGHTCOVE_PLAYER_BLOG_ACCOUNT_ID
                      ? BRIGHTCOVE_VIDEO_BLOG_PLAYER_ID
                      : BRIGHTCOVE_VIDEO_PLAYER_ID
                    : BRIGHTCOVE_VIDEO_PLAYER_ID
                }
              />
            </div>
          </div>
        )}
        <div
          className={removeSpaces(
            `\
              u-flex \
              u-flexCol \
              ${
                item && parameters.derived?.isSteps
                  ? 'u-size5of8'
                  : 'u-sizeFull'
              } \
              ${
                blurb.imageUrl &&
                parameters.derived?.isGallery &&
                !parameters.derived?.isCard
                  ? 'u-sm-size3of4 u-md-size3of4'
                  : ''
              } \
              ${styles.alignment || ''} \
              ${
                parameters.style === 'Callout'
                  ? parameters.derived?.isSteps
                    ? 'u-paddingTop2gu'
                    : ''
                  : ''
              } \
              `
          )}
        >
          {blurb.itemPrefix &&
            parameters.derived?.isCard &&
            parameters.derived?.isGallery && (
              <div className="u-marginBottom2gu u-marginTop2gu">
                <span className="u-textSecondary u-textBold u-textColorLight  u-textSizeMsrm1 u-paddingTop1gu u-borderTop u-borderColorPrimary u-borderWidthMedium">
                  {blurb.itemPrefix}
                </span>
              </div>
            )}
          {blurb.subtitle && parameters.derived?.isInset && (
            <Subtitle
              text={blurb.subtitle}
              textColor={
                blurb.imageUrl && parameters.derived?.isInset
                  ? 'u-textColorWhite'
                  : null
              }
              lineColor="u-bgColorLight"
              mode="line"
              textAlign={styles.textAlign}
              textSize="u-textSizeMsrp1"
            />
          )}
          {_titleObject ? (
            <div className="u-sizeFull">
              <input
                type="text"
                placeholder={_titleObject.placeholder}
                className="Input u-sizeFull u-padding0 u-border0 u-bgColorTransparent u-textSizeMsrp2 u-textSecondary"
                onKeyPress={(e) =>
                  onKeyPressSearch(
                    e,
                    _titleObject.type === 'url' && _href
                      ? _href.replaceAll(
                          '{' + _titleObject.variable + '}',
                          encodeForURL(e.target.value)
                        )
                      : null
                  )
                }
              />
            </div>
          ) : (
            blurb.title &&
            parameters.style !== 'Gray' && (
              <div
                className={`${
                  !item &&
                  !parameters.derived?.isGallery &&
                  parameters.style !== 'Callout'
                    ? 'u-marginBottom2gu'
                    : ''
                }`}
              >
                {_href && !parameters.derived?.isInset ? (
                  <Link2
                    url={_href}
                    text={blurb.title}
                    className={`u-flex ${
                      item && parameters.derived?.isLinks ? '' : 'u-textBold'
                    } ${styles.capitalization || ''}`}
                    data-analytics={
                      parameters?.derived?.isAccordion && item?.groupName
                        ? item?.groupName
                            .replaceAll('.', '')
                            .replaceAll(' ', '-')
                            .toLowerCase() + '-sub-links'
                        : undefined
                    }
                    {...analyticsAttrFormatter(blurb.link.description)}
                    data-qa-automation={
                      _href.includes('/leadership/') ? 'leader-name' : undefined
                    }
                  >
                    {renderTitle(true)}
                  </Link2>
                ) : (
                  renderTitle()
                )}
              </div>
            )
          )}
          <div
            className={
              parameters.derived?.isGallery &&
              parameters.derived?.isCard &&
              (blurb.publicationDate || blurb.subtitle || blurb.subtitle2)
                ? 'u-marginTop2gu'
                : ''
            }
          >
            {blurb.publicationDate &&
              !(item && parameters.derived?.isLinks) && (
                <Subtitle
                  text={new Date(blurb.publicationDate).toLocaleDateString(
                    'en-us',
                    { year: 'numeric', month: 'long', day: 'numeric' }
                  )}
                  mode="small-bold"
                  className={
                    parameters.derived?.isGallery && parameters.derived?.isCard
                      ? 'u-inline'
                      : ''
                  }
                />
              )}
            {parameters.derived?.isGallery &&
              parameters.derived?.isCard &&
              blurb.publicationDate &&
              (blurb.subtitle2 || blurb.subtitle) && (
                <Subtitle
                  text="|"
                  mode="small"
                  textAlign={styles.textAlign}
                  className="u-inline u-marginLeft1gu u-marginRight1gu"
                />
              )}
            {(blurb.subtitle || blurb.subtitle2) &&
              !parameters.derived?.isInset &&
              (item || parameters.style === 'Callout' ? (
                <Subtitle
                  text={blurb.subtitle2 || blurb.subtitle}
                  mode="small"
                  textAlign={styles.textAlign}
                  textSize={!blurb.publicationDate ? 'u-textSizeMsr' : null}
                  className={
                    parameters.derived?.isGallery && parameters.derived?.isCard
                      ? 'u-inline'
                      : ''
                  }
                />
              ) : (
                <Subtitle
                  text={blurb.subtitle2 || blurb.subtitle}
                  mode="medium"
                  textAlign={styles.textAlign}
                  className={
                    parameters.derived?.isGallery && parameters.derived?.isCard
                      ? 'u-inline'
                      : ''
                  }
                />
              ))}
          </div>
        </div>
        <div
          className={`u-flex u-flexCol ${
            parameters.derived?.isGallery ? 'u-flexAuto' : ''
          } ${
            item &&
            parameters.derived?.isSteps &&
            parameters.style === 'Callout'
              ? 'u-size5of8'
              : 'u-sizeFull'
          }`}
        >
          {(blurb.summary || blurb.html) && parameters.style !== 'Gallery' && (
            <div
              className={removeSpaces(
                `\
                    Content \
                    u-flexAuto \
                    u-paddingBottom0 \
                    ${
                      parameters.style !== 'Callout'
                        ? ''
                        : 'u-sm-marginTop3gu u-md-marginTop3gu u-lg-marginLeft8gu u-md-marginLeft0 u-sm-marginLeft0'
                    } \
                    ${
                      parameters.derived?.isGallery &&
                      parameters.alignment === 'Center'
                        ? 'u-paddingHoriz2gu'
                        : ''
                    } \
                    ${parameters.derived?.isInset ? 'u-textColorWhite' : ''} \
                    ${styles.textAlign} \
                    `
              )}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  page.rewriteLinks(blurb.summary || blurb.html),
                  { ADD_TAGS: ['embed'], ADD_ATTR: ['target'] }
                ),
              }}
            />
          )}
          {blurb.time && blurb.videoId && !parameters.derived?.isGallery && (
            <IconText
              icon={blurb.time}
              text={blurb.time}
              textColor="u-textColorBrand"
              iconColor="u-textColorBrandLight"
              className="u-marginTop8gu u-marginBottom8gu"
            />
          )}
          {((blurb.link?.text && (!parameters.derived?.isGallery || item)) ||
            (parameters.subscription && !item)) && (
            <div
              className={`u-flex u-flexAlignItemsCenter ${
                parameters.subscription === 'Education Savings'
                  ? 'u-marginTop1gu'
                  : 'u-marginTop5gu'
              }`}
            >
              {parameters.subscription && !item && (
                <div
                  className={`${
                    blurb.link?.text && (!parameters.derived?.isGallery || item)
                      ? 'u-paddingRight2gu'
                      : ''
                  }`}
                >
                  <email-subscription-modal
                    data-use-inline-button="true"
                    data-subscription-name={parameters.subscription}
                  />
                </div>
              )}
              {blurb.link?.text &&
                (!parameters.derived?.isGallery || item) &&
                !blurb.link.asButton && (
                  <div
                    className={`${
                      parameters.subscription && !item
                        ? 'u-paddingLeft2gu u-borderLeft u-decoBorderResetTBR'
                        : ''
                    }`}
                  >
                    <Link2
                      url={_href}
                      text={blurb.link.text}
                      icon={blurb.link.icon}
                      arrow={styles.append}
                      className={`${styles.button || ''} ${styles.hover}`}
                      textClasses={`${
                        item &&
                        parameters.derived?.isSteps &&
                        parameters.style === 'Callout'
                          ? 'u-lg-marginLeft8gu u-md-marginLeft0 u-sm-marginLeft0'
                          : ''
                      } u-textSecondary u-textKernLoose`}
                      {...analyticsAttrFormatter(blurb.link.description)}
                    />
                  </div>
                )}
              {blurb.link?.text &&
                (!parameters.derived?.isGallery || item) &&
                blurb.link.asButton && (
                  <Link2
                    url={_href}
                    text={blurb.link.text}
                    className={`${'Button Button--secondary u-textColorBrand u-sizeFull u-flexJustifyCenter'} ${
                      styles.hover
                    }`}
                    textClasses={`u-textSecondary u-textKernLoose`}
                    parentClasses="u-sizeFull"
                    {...analyticsAttrFormatter(blurb.link.description)}
                  />
                )}
            </div>
          )}
          {blurb.linkList &&
          blurb.linkList.length > 0 &&
          !parameters.derived?.isGallery &&
          !(item && parameters.derived?.isLinks) ? (
            parameters.derived?.isSteps ? (
              <div className="u-flex u-flexJustifyAround u-marginTop10gu">
                {blurb.linkList.map((link, index) => (
                  <React.Fragment key={index}>
                    {link?.text && link?.url && (
                      <div
                        key={index}
                        className={`u-padding5gu u-decoBorderLightFull ${
                          index > 0 ? 'u-marginLeft10gu' : ''
                        }`}
                        style={{ maxWidth: 375 }}
                      >
                        <Link2
                          url={getLinkUrl(link)}
                          text={link.text}
                          textClasses="u-textSecondary u-textBold u-textSizeMsrp1"
                        />
                        <div className="Content u-marginVert3gu">
                          {link.description}
                        </div>
                        <div className="u-paddingTop3gu u-decoBorderLight u-decoBorderResetBottom">
                          <Link2
                            url={getLinkUrl(link)}
                            text={
                              link.icon === 'info'
                                ? 'Download'
                                : link.icon === 'email' || link.icon === 'phone'
                                  ? 'Contact Us'
                                  : link.text
                            }
                            icon={link.icon}
                            textClasses="u-textSecondary"
                          />
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            ) : (
              <LinkList
                linkList={blurb.linkList}
                buttonClass="u-bgColorBrandDark u-textColorWhite u-border0"
              />
            )
          ) : null}
          {((blurb.keys && blurb.keys.length > 0) ||
            (blurb.linkList && blurb.linkList.length > 0)) &&
            parameters.derived?.isGallery && (
              <div>
                <hr />
                <div className="u-flex">
                  <div
                    className={`${
                      blurb.keys && blurb.keys.length > 0 ? 'u-size3of8' : ''
                    }`}
                  >
                    {blurb.itemType && (
                      <IconText icon={blurb.itemType} text={blurb.itemType} />
                    )}
                    {blurb.time && (
                      <IconText
                        icon={blurb.time}
                        text={blurb.time}
                        className={blurb.itemType ? 'u-marginTop1gu' : null}
                      />
                    )}
                    {blurb.keys?.indexOf('client-materials') > -1 && (
                      <IconText
                        icon={'Client materials'}
                        text={'Client materials'}
                        className={
                          blurb.itemType || blurb.time ? 'u-marginTop1gu' : null
                        }
                      />
                    )}
                    {blurb.linkList &&
                      !blurb.imageUrl &&
                      blurb.linkList.map((link, index) => (
                        <React.Fragment key={index}>
                          {link?.text && link?.url && (
                            <div
                              key={index}
                              className={`u-marginTop2gu`}
                              style={{ paddingLeft: 2 }}
                            >
                              <Link2
                                url={
                                  link.url === 'mailto:'
                                    ? `mailto:?subject=${blurb.title}&body=${
                                        blurb.title + '%0D%0A%0D%0A' + _href
                                      }`
                                    : link.url.startsWith('mailto:')
                                      ? link.url
                                      : getLinkUrl(link)
                                }
                                text={link.text}
                                icon={link.icon}
                                className={`u-textNoWrap u-textSizeMsrm1 u-textBrandLight u-textSecondary u-textKernLoose`}
                              />
                            </div>
                          )}
                        </React.Fragment>
                      ))}
                  </div>
                  {blurb.keys && blurb.keys.length > 0 && (
                    <div
                      className="u-flex u-flexCol u-flexAlignItemsEnd u-size5of8"
                      style={{ minHeight: 76 }}
                    >
                      {blurb.keys.map((key, index) => (
                        <React.Fragment key={key}>
                          {
                            // TODO: Hardcode of 'client-materials'
                            key && key !== 'client-materials' && (
                              <div
                                className="u-textColorBrandLight u-textItalic u-textCapitalize u-textSizeMsrm1"
                                style={{
                                  marginTop: index > 0 ? 5 : 2,
                                  paddingTop: 2,
                                  paddingBottom: 2,
                                }}
                              >
                                <Link2
                                  className="u-textRight"
                                  url={
                                    getSiteName() === 'cti-institutional'
                                      ? getLocalPath() +
                                        `/insights/latest-insights?topic=${key}`
                                      : `${window.location.pathname}?topic=${key}`
                                  }
                                  text={
                                    (taxonomies?.length > 0 &&
                                      taxonomies.find(
                                        (taxonomy) => taxonomy.key === key
                                      )?.name) ||
                                    key.replaceAll('-', ' ')
                                  }
                                />
                              </div>
                            )
                          }
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  )
}
